import type {
    Appointment,
    AppointmentSettings,
    ClinicData,
    FollowUp,
    FollowUpBucket,
    Invoice,
    Patient,
    PaymentStatus,
    TreatmentRef,
    Visit
} from '@/types/clinic'

import {
    addDays,
    addMonths,
    ageFromDob,
    dayOfWeek,
    eachDay,
    formatShortDate,
    formatShortMonth,
    formatTime,
    monthKey,
    startOfMonth,
    startOfWeek,
    timeToMinutes
} from '@/lib/dates'
import { normalizePhone, roundMoney } from '@/lib/format'
import { OPEN_APPOINTMENT_STATUSES, WEEKDAYS } from '@/constants/clinic'

export interface InvoiceSummary {
    subtotal: number
    discount: number
    taxable: number
    tax: number
    total: number
    paid: number
    balance: number
    status: PaymentStatus
}

export const getInvoiceSummary = (invoice: Pick<Invoice, 'items' | 'discountType' | 'discountValue' | 'taxRate' | 'payments'>): InvoiceSummary => {
    const subtotal = roundMoney(invoice.items.reduce((sum, item) => sum + (item.unitPrice || 0) * (item.quantity || 0), 0))
    const discountValue = Math.max(invoice.discountValue || 0, 0)
    const rawDiscount = invoice.discountType === 'percent' ? subtotal * Math.min(discountValue, 100) / 100 : discountValue
    const discount = roundMoney(Math.min(rawDiscount, subtotal))
    const taxable = roundMoney(subtotal - discount)
    const tax = roundMoney(taxable * Math.max(invoice.taxRate || 0, 0) / 100)
    const total = roundMoney(taxable + tax)
    const paid = roundMoney(invoice.payments.reduce((sum, payment) => sum + (payment.amount || 0), 0))
    const balance = roundMoney(Math.max(total - paid, 0))
    const status: PaymentStatus = total <= 0 || paid >= total ? 'paid' : paid > 0 ? 'partial' : 'pending'

    return { subtotal, discount, taxable, tax, total, paid, balance, status }
}

export const indexById = <T extends { id: string }>(items: T[]): Map<string, T> => {
    return new Map(items.map(item => [item.id, item]))
}

export const byDateDesc = (a: { date: string, createdAt: string }, b: { date: string, createdAt: string }): number => {
    return b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt)
}

export const treatmentNames = (refs: TreatmentRef[]): string => refs.map(ref => ref.name).join(', ')

export const getPatientAge = (patient: Pick<Patient, 'dob' | 'age'>, today: string): number | null => {
    if (patient.dob) return ageFromDob(patient.dob, today)
    return patient.age
}

export const matchesPatientQuery = (patient: Patient, query: string): boolean => {
    const value = query.trim().toLowerCase()
    if (!value) return true
    if (patient.name.toLowerCase().includes(value)) return true
    if (patient.code.toLowerCase().includes(value)) return true
    if (patient.place.toLowerCase().includes(value)) return true
    const digits = value.replace(/\D/g, '')
    if (digits.length >= 3) {
        const needle = normalizePhone(digits).length === 10 ? normalizePhone(digits) : digits
        return patient.phone.includes(needle) || patient.altPhone.includes(needle)
    }
    return false
}

export interface PatientVisitStats {
    count: number
    lastVisit: Visit | null
}

export const getVisitStatsByPatient = (visits: Visit[]): Map<string, PatientVisitStats> => {
    const stats = new Map<string, PatientVisitStats>()
    for (const visit of visits) {
        const current = stats.get(visit.patientId) ?? { count: 0, lastVisit: null }
        current.count += 1
        if (!current.lastVisit || byDateDesc(visit, current.lastVisit) < 0) current.lastVisit = visit
        stats.set(visit.patientId, current)
    }
    return stats
}

export const getInvoiceByVisit = (invoices: Invoice[]): Map<string, Invoice> => {
    const map = new Map<string, Invoice>()
    for (const invoice of invoices) {
        if (invoice.visitId) map.set(invoice.visitId, invoice)
    }
    return map
}

export const getVisitPaymentStatus = (visit: Visit, invoiceByVisit: Map<string, Invoice>): PaymentStatus => {
    const invoice = invoiceByVisit.get(visit.id)
    return invoice ? getInvoiceSummary(invoice).status : visit.paymentStatus
}

export const getFollowUpBucket = (followUp: FollowUp, today: string): FollowUpBucket => {
    if (followUp.status === 'completed') return 'completed'
    if (followUp.status === 'cancelled') return 'cancelled'
    if (followUp.dueDate < today) return 'overdue'
    if (followUp.dueDate === today) return 'today'
    if (followUp.dueDate === addDays(today, 1)) return 'tomorrow'
    return 'upcoming'
}

export const isOpenAppointment = (appointment: Appointment): boolean => {
    return OPEN_APPOINTMENT_STATUSES.includes(appointment.status)
}

export const byAppointmentTime = (a: Appointment, b: Appointment): number => {
    return a.date.localeCompare(b.date) || a.time.localeCompare(b.time)
}

// Appointment conflicts

export interface AppointmentCandidate {
    id?: string
    patientId: string
    date: string
    time: string
    duration: number
}

export interface AppointmentConflicts {
    duplicate: Appointment | null
    samePatientSameDay: Appointment[]
    overlapping: Appointment[]
}

const rangesOverlap = (startA: number, endA: number, startB: number, endB: number): boolean => startA < endB && startB < endA

export const findAppointmentConflicts = (appointments: Appointment[], candidate: AppointmentCandidate): AppointmentConflicts => {
    const result: AppointmentConflicts = { duplicate: null, samePatientSameDay: [], overlapping: [] }
    if (!candidate.date || !candidate.time) return result

    const start = timeToMinutes(candidate.time)
    const end = start + Math.max(candidate.duration || 0, 5)
    const sameDay = appointments.filter(item => item.id !== candidate.id
        && item.date === candidate.date
        && item.status !== 'cancelled'
        && item.status !== 'no-show')

    for (const item of sameDay) {
        const itemStart = timeToMinutes(item.time)
        const itemEnd = itemStart + Math.max(item.duration || 0, 5)
        const overlaps = rangesOverlap(start, end, itemStart, itemEnd)
        if (item.patientId === candidate.patientId) {
            if (overlaps && !result.duplicate) result.duplicate = item
            else if (!overlaps) result.samePatientSameDay.push(item)
        }
        else if (overlaps) {
            result.overlapping.push(item)
        }
    }

    return result
}

export const getScheduleWarnings = (settings: AppointmentSettings, date: string, time: string, duration: number): string[] => {
    const warnings: string[] = []
    if (!date || !time) return warnings

    const weekday = dayOfWeek(date)
    if (!settings.workingDays.includes(weekday)) {
        const label = WEEKDAYS.find(day => day.value === weekday)?.label ?? 'that day'
        warnings.push(`The clinic is closed on ${label}s.`)
    }

    const start = timeToMinutes(time)
    const end = start + duration
    if (start < timeToMinutes(settings.openTime) || end > timeToMinutes(settings.closeTime)) {
        warnings.push(`This is outside clinic hours (${formatTime(settings.openTime)} to ${formatTime(settings.closeTime)}).`)
    }

    if (settings.breakStart && settings.breakEnd && rangesOverlap(start, end, timeToMinutes(settings.breakStart), timeToMinutes(settings.breakEnd))) {
        warnings.push(`This overlaps the break (${formatTime(settings.breakStart)} to ${formatTime(settings.breakEnd)}).`)
    }

    return warnings
}

export const getDaySlots = (settings: AppointmentSettings): string[] => {
    const slots: string[] = []
    const step = Math.max(settings.slotDuration || 30, 5)
    const open = timeToMinutes(settings.openTime)
    const close = timeToMinutes(settings.closeTime)
    const breakStart = settings.breakStart ? timeToMinutes(settings.breakStart) : -1
    const breakEnd = settings.breakEnd ? timeToMinutes(settings.breakEnd) : -1
    for (let minutes = open; minutes + step <= close; minutes += step) {
        if (breakStart >= 0 && rangesOverlap(minutes, minutes + step, breakStart, breakEnd)) continue
        const hours = Math.floor(minutes / 60)
        const rest = minutes % 60
        slots.push(`${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`)
    }
    return slots
}

export const getFreeSlots = (settings: AppointmentSettings, appointments: Appointment[], date: string, duration: number, ignoreId?: string): string[] => {
    const busy = appointments
        .filter(item => item.id !== ignoreId && item.date === date && item.status !== 'cancelled' && item.status !== 'no-show')
        .map(item => [timeToMinutes(item.time), timeToMinutes(item.time) + Math.max(item.duration, 5)])

    return getDaySlots(settings).filter(slot => {
        const start = timeToMinutes(slot)
        const end = start + Math.max(duration, 5)
        return !busy.some(([busyStart, busyEnd]) => rangesOverlap(start, end, busyStart, busyEnd))
    })
}

// Dashboard

export interface DashboardStats {
    totalPatients: number
    newPatientsThisMonth: number
    todaysAppointments: number
    upcomingAppointments: number
    returningToday: number
    pendingFollowUps: number
    overdueFollowUps: number
    todaysRevenue: number
    pendingPayments: number
    pendingInvoices: number
}

export const getPaymentsOn = (invoices: Invoice[], from: string, to: string): number => {
    let total = 0
    for (const invoice of invoices) {
        for (const payment of invoice.payments) {
            if (payment.date >= from && payment.date <= to) total += payment.amount
        }
    }
    return roundMoney(total)
}

export const getDashboardStats = (data: ClinicData, today: string): DashboardStats => {
    const month = monthKey(today)
    const pendingFollowUps = data.followUps.filter(item => item.status === 'pending')
    let pendingPayments = 0
    let pendingInvoices = 0
    for (const invoice of data.invoices) {
        const summary = getInvoiceSummary(invoice)
        if (summary.balance > 0) {
            pendingPayments += summary.balance
            pendingInvoices += 1
        }
    }

    return {
        totalPatients: data.patients.length,
        newPatientsThisMonth: data.patients.filter(item => monthKey(item.registeredOn) === month).length,
        todaysAppointments: data.appointments.filter(item => item.date === today && item.status !== 'cancelled').length,
        upcomingAppointments: data.appointments.filter(item => item.date > today && isOpenAppointment(item)).length,
        returningToday: pendingFollowUps.filter(item => item.dueDate === today).length,
        pendingFollowUps: pendingFollowUps.length,
        overdueFollowUps: pendingFollowUps.filter(item => item.dueDate < today).length,
        todaysRevenue: getPaymentsOn(data.invoices, today, today),
        pendingPayments: roundMoney(pendingPayments),
        pendingInvoices
    }
}

// Chart series

export const getMonthKeys = (today: string, months: number): string[] => {
    const first = startOfMonth(addMonths(startOfMonth(today), -(months - 1)))
    return Array.from({ length: months }, (_, index) => monthKey(addMonths(first, index)))
}

export const getMonthKeysBetween = (from: string, to: string): string[] => {
    const keys: string[] = []
    let cursor = startOfMonth(from)
    while (monthKey(cursor) <= monthKey(to) && keys.length < 240) {
        keys.push(monthKey(cursor))
        cursor = addMonths(cursor, 1)
    }
    return keys
}

const monthLabel = (key: string): string => formatShortMonth(`${key}-01`)

export interface DailyCountPoint {
    date: string
    label: string
    value: number
}

export const getDailyVisits = (visits: Visit[], from: string, to: string): DailyCountPoint[] => {
    const counts = new Map<string, number>()
    for (const visit of visits) {
        if (visit.date >= from && visit.date <= to) counts.set(visit.date, (counts.get(visit.date) ?? 0) + 1)
    }
    return eachDay(from, to).map(date => ({ date, label: formatShortDate(date), value: counts.get(date) ?? 0 }))
}

export const getWeeklyVisits = (visits: Visit[], from: string, to: string): DailyCountPoint[] => {
    const counts = new Map<string, number>()
    for (const visit of visits) {
        if (visit.date >= from && visit.date <= to) {
            const week = startOfWeek(visit.date)
            counts.set(week, (counts.get(week) ?? 0) + 1)
        }
    }
    const points: DailyCountPoint[] = []
    let cursor = startOfWeek(from)
    while (cursor <= to && points.length < 120) {
        points.push({ date: cursor, label: formatShortDate(cursor), value: counts.get(cursor) ?? 0 })
        cursor = addDays(cursor, 7)
    }
    return points
}

export interface MonthlyCountPoint {
    month: string
    label: string
    value: number
}

export const getMonthlyVisits = (visits: Visit[], keys: string[]): MonthlyCountPoint[] => {
    const counts = new Map<string, number>()
    for (const visit of visits) counts.set(monthKey(visit.date), (counts.get(monthKey(visit.date)) ?? 0) + 1)
    return keys.map(key => ({ month: key, label: monthLabel(key), value: counts.get(key) ?? 0 }))
}

export interface NewReturningPoint {
    month: string
    label: string
    newPatients: number
    returning: number
}

export const getNewVsReturning = (patients: Patient[], visits: Visit[], keys: string[]): NewReturningPoint[] => {
    const registered = new Map(patients.map(patient => [patient.id, patient.registeredOn]))
    return keys.map(key => {
        const newPatients = patients.filter(patient => monthKey(patient.registeredOn) === key).length
        const returning = new Set(visits
            .filter(visit => monthKey(visit.date) === key && (registered.get(visit.patientId) ?? '') < `${key}-01`)
            .map(visit => visit.patientId)).size
        return { month: key, label: monthLabel(key), newPatients, returning }
    })
}

export interface NamedCount {
    name: string
    value: number
}

export const getTreatmentCounts = (visits: Visit[], from = '', to = '9999-12-31'): NamedCount[] => {
    const counts = new Map<string, number>()
    for (const visit of visits) {
        if (visit.date < from || visit.date > to) continue
        for (const ref of visit.treatments) counts.set(ref.name, (counts.get(ref.name) ?? 0) + 1)
    }
    return Array.from(counts, ([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value || a.name.localeCompare(b.name))
}

export const getDailyRevenue = (invoices: Invoice[], from: string, to: string): DailyCountPoint[] => {
    const totals = new Map<string, number>()
    for (const invoice of invoices) {
        for (const payment of invoice.payments) {
            if (payment.date >= from && payment.date <= to) totals.set(payment.date, (totals.get(payment.date) ?? 0) + payment.amount)
        }
    }
    return eachDay(from, to).map(date => ({ date, label: formatShortDate(date), value: roundMoney(totals.get(date) ?? 0) }))
}

export const getMonthlyRevenue = (invoices: Invoice[], keys: string[]): MonthlyCountPoint[] => {
    const totals = new Map<string, number>()
    for (const invoice of invoices) {
        for (const payment of invoice.payments) {
            const key = monthKey(payment.date)
            totals.set(key, (totals.get(key) ?? 0) + payment.amount)
        }
    }
    return keys.map(key => ({ month: key, label: monthLabel(key), value: roundMoney(totals.get(key) ?? 0) }))
}

export interface FollowUpCompletion {
    due: number
    completed: number
    pending: number
    rate: number
}

export const getFollowUpCompletion = (followUps: FollowUp[], from: string, to: string, today: string): FollowUpCompletion => {
    const end = to < today ? to : today
    const relevant = followUps.filter(item => item.status !== 'cancelled' && item.dueDate >= from && item.dueDate <= end)
    const completed = relevant.filter(item => item.status === 'completed').length
    const due = relevant.length
    return { due, completed, pending: due - completed, rate: due ? (completed / due) * 100 : 0 }
}
